import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useProfiles } from "@/hooks/useProfiles";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Pencil, Eye, Link2, Trash2, AlertCircle } from "lucide-react";
import BulkUserUpload from "@/components/admin/BulkUserUpload";
import BulkProfileImageUpload from "@/components/admin/BulkProfileImageUpload";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile(user?.id);
  const { profiles, loading: profilesLoading, deleteUser } = useProfiles();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{
    id: string;
    username: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);

  console.log("AdminDashboard - Auth user:", user?.id);
  console.log("AdminDashboard - Profile:", profile);
  console.log("AdminDashboard - Is admin:", profile?.is_admin);

  useEffect(() => {
    if (!profileLoading && profile && !profile.is_admin) {
      console.log("User is not an admin:", profile);
      setAdminError("You don't have admin permissions to view this page.");
      toast({
        title: "Access Denied",
        description: "You don't have admin permissions to view this page.",
        variant: "destructive",
      });
    }
  }, [profile, profileLoading, toast]);

  const handleCopyClaimLink = () => {
    const claimLink = `${window.location.origin}/claim-profile`;
    navigator.clipboard
      .writeText(claimLink)
      .then(() => {
        toast({
          title: "Link Copied",
          description: "Claim profile link has been copied to your clipboard.",
        });
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        toast({
          title: "Failed to Copy",
          description: "Please try again or copy the link manually.",
          variant: "destructive",
        });
      });
  };

  const openDeleteDialog = (profileId: string, username: string) => {
    setUserToDelete({ id: profileId, username: username || "this user" });
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      const success = await deleteUser(userToDelete.id);

      if (success) {
        toast({
          title: "User Deleted",
          description: `${userToDelete.username} has been successfully deleted.`,
        });
      } else {
        throw new Error("Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  if (authLoading || profileLoading || profilesLoading)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );

  if (!user) return <Navigate to="/auth" replace />;

  if (adminError || !profile.is_admin) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-lg">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have admin permissions to view this page.
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => (window.location.href = "/")}
        >
          Back to Home
        </Button>
      </div>
    );
  }

  const defaultAvatar = "/lovable-uploads/default-avatar.png";

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center p-4">
      <div className="w-full max-w-6xl">
        <div className="mb-8 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCopyClaimLink}>
              <Link2 className="mr-2 h-4 w-4" />
              Copy Claim Link
            </Button>
            <Button
              variant="outline"
              onClick={() => (window.location.href = "/")}
            >
              Back to Home
            </Button>
          </div>
        </div>

        <div className="space-y-8">
          <BulkUserUpload />

          <BulkProfileImageUpload />

          <Card>
            <CardHeader>
              <CardTitle>User Management</CardTitle>
              <CardDescription>
                View profiles, or edit user information.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="py-3 px-4 text-left font-medium">User</th>
                      <th className="py-3 px-4 text-left font-medium">Email</th>
                      <th className="py-3 px-4 text-left font-medium">
                        Location
                      </th>
                      <th className="py-3 px-4 text-left font-medium">
                        Profession
                      </th>
                      <th className="py-3 px-4 text-left font-medium">Admin</th>
                      <th className="py-3 px-4 text-left font-medium">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.map((profile) => (
                      <tr
                        key={profile.id}
                        className="border-b hover:bg-muted/30 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage
                                src={profile.avatar_url || defaultAvatar}
                                alt={profile.username || "User"}
                              />
                              <AvatarFallback>
                                {getInitials(profile.username)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">
                              {profile.username || "Anonymous"}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {profile.email || "-"}
                        </td>
                        <td className="py-3 px-4">{profile.location || "-"}</td>
                        <td className="py-3 px-4">
                          {profile.profession || "-"}
                        </td>
                        <td className="py-3 px-4">
                          {profile.is_admin ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Admin
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Link to={`/profile/${profile.id}`}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <Eye className="h-4 w-4" />
                                <span className="sr-only">View Profile</span>
                              </Button>
                            </Link>
                            <Link to={`/admin/edit/${profile.id}`}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <Pencil className="h-4 w-4" />
                                <span className="sr-only">Edit Profile</span>
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                              onClick={() =>
                                openDeleteDialog(profile.id, profile.username)
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete User</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm User Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.username}? This
              action cannot be undone and will remove all user data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex space-x-2 py-3">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
